import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../assets/styles/Dashboard.css';
import '../../assets/styles/Survey.css';
import CreateSurveyModal from './CreateSurvey/CreateSurveyModal';
import TokenStatusNotification from '../common/TokenStatusNotification';
import { trackEvent } from '../../utils/analytics';
import { useLanguage } from '../../assets/js/LanguageContext';
import { localeOf } from '../../assets/js/locale';
import { sendPinEmails } from '../../utils/pinEmails';
import {
  ArrowUpDown, BarChart3, CalendarDays, Check, ClipboardList, Copy, FileDown, Info, ListChecks,
  MessageSquareText, Pencil, Plus, Presentation, RotateCw, Search, Share2, Sparkles, Trash2, TriangleAlert, Users
} from 'lucide-react';
import SectionEyebrow from '../common/SectionEyebrow';
import ActionMenu from '../common/ActionMenu';
import SelectMenu from '../common/SelectMenu';
import Pagination from '../common/Pagination';
import {
  DeleteSurveyModal, EditSurveyModal, PresentationModal, QuestionsModal, ShareSurveyModal, parseParticipants, responsesText
} from './DashboardModals';
import useSurveyPdf from './useSurveyPdf';
import '../../assets/styles/DashboardList.css';
import AppModal from '../common/Modal';
import Loader from '../common/Loader';

const STATUS_FILTERS = ['all', 'open', 'results_available', 'no_data'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PAGE_SIZE_KEY = 'tp-dash-page-size';

const readPageSize = () => {
  try {
    const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : PAGE_SIZE_OPTIONS[0];
  } catch (error) {
    return PAGE_SIZE_OPTIONS[0];
  }
};

const normalizeSurveysResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.encuestas)) return payload.encuestas;
  if (Array.isArray(payload?.surveys)) return payload.surveys;
  if (Array.isArray(payload?.data)) return payload.data;
  console.warn('Respuesta de encuestas inesperada:', payload);
  return [];
};

const Dashboard = () => {
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [sortOption, setSortOption] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readPageSize);
  const toolbarRef = useRef(null);
  const handledEncuestaRef = useRef(null);
  const [surveysLoaded, setSurveysLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [copiedPinId, setCopiedPinId] = useState(null);
  const [isShareModalOpen2, setIsShareModalOpen2] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSurvey, setEditSurvey] = useState({ id: null, titulo: '', fecha_inicio: '', fecha_fin: '' });
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);
  const [presentationSurvey, setPresentationSurvey] = useState(null);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [questionsModalSurvey, setQuestionsModalSurvey] = useState(null);
  const [questionsModalData, setQuestionsModalData] = useState([]);
  const [questionsModalLoading, setQuestionsModalLoading] = useState(false);
  const [questionsModalError, setQuestionsModalError] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [currentTitulo, setCurrentTitulo] = useState('');
  const [showCreateSurveyModal, setShowCreateSurveyModal] = useState(false);
  const [notice, setNotice] = useState(null);
  const [savedQuestions, setSavedQuestions] = useState(null);
  const [savedTopic, setSavedTopic] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSurvey, setShareSurvey] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [highlightedSurveyId, setHighlightedSurveyId] = useState(null);
  const { t, language } = useLanguage();
  const { downloadPdf, pdfUi } = useSurveyPdf();

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchTerm, sortOption, pageSize]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const encuestaId = params.get('encuesta');
    if (!encuestaId || handledEncuestaRef.current === location.search) return undefined;
    if (!Array.isArray(filteredSurveys) || filteredSurveys.length === 0) return undefined;

    const index = filteredSurveys.findIndex((survey) => String(survey.id_encuesta) === encuestaId);
    if (index === -1) return undefined;
    if (statusFilter !== 'all') {
      setStatusFilter('all');
      return undefined;
    }
    const targetPage = Math.floor(index / pageSize) + 1;
    if (page !== targetPage) {
      setPage(targetPage);
      return undefined;
    }

    const target = document.getElementById(`encuesta-row-${encuestaId}`);
    if (!target) return undefined;
    handledEncuestaRef.current = location.search;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedSurveyId(Number(encuestaId));
    const timer = setTimeout(() => setHighlightedSurveyId(null), 2500);
    return () => clearTimeout(timer);
  }, [location.search, filteredSurveys, statusFilter, page, pageSize]);

  useEffect(() => {
    fetchSurveys();
    
    // Verificar si hay preguntas guardadas del generador de la página principal
    const savedQuestionsData = localStorage.getItem('selectedQuestions');
    const savedTopicData = localStorage.getItem('surveyTopic');
    
    if (savedQuestionsData && savedTopicData) {
      setSavedQuestions(JSON.parse(savedQuestionsData));
      setSavedTopic(savedTopicData);
      setShowCreateSurveyModal(true);
    }


  }, []);



  // Actualizar participantes en tiempo real para todas las encuestas
  useEffect(() => {
    const updateAllParticipantsInterval = setInterval(async () => {
      // Con la pestaña en segundo plano no hace falta ir preguntando al servidor
      if (document.hidden) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSurveys(normalizeSurveysResponse(data));
          setLoadError(false);
        } else if (res.status === 401 || res.status === 403) {
          setSurveys([]);
        }
      } catch (error) {
        console.error('Error al actualizar todas las encuestas:', error);
      }
    }, 10000); // Actualizar cada 10 segundos

    return () => clearInterval(updateAllParticipantsInterval);
  }, []);

  useEffect(() => {
    let sorted = Array.isArray(surveys) ? [...surveys] : [];
    
    if (sortOption === 'titulo') sorted.sort((a, b) => a.titulo.localeCompare(b.titulo));
    if (sortOption === 'fecha_inicio') sorted.sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio));
    if (sortOption === 'fecha_fin') sorted.sort((a, b) => new Date(a.fecha_fin) - new Date(b.fecha_fin));

    if (searchTerm) {
      sorted = sorted.filter(s =>
        s.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.pin.toString().includes(searchTerm)
      );
    }
    setFilteredSurveys(sorted);
  }, [surveys, sortOption, searchTerm]);

  const fetchSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        setSurveys([]);
        return;
      }
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setSurveys(normalizeSurveysResponse(await res.json()));
      setLoadError(false);
    } catch (error) {
      // Un fallo del servidor no es "no tienes diagnosticos": se avisa y se deja reintentar
      console.error('Error al cargar encuestas:', error);
      setLoadError(true);
    } finally {
      setSurveysLoaded(true);
    }
  };

  const handleDelete = (survey) => {
    setCurrentPin(survey.pin);
    setCurrentTitulo(survey.titulo);
    setIsShareModalOpen2(true);
  };

  const handlePresentation = (survey) => {
    setPresentationSurvey(survey);
    setIsPresentationModalOpen(true);
  };

  // Ver (en solo lectura) las preguntas de una encuesta, esté lanzada o no.
  const handleViewQuestions = async (survey) => {
    setQuestionsModalSurvey(survey);
    setIsQuestionsModalOpen(true);
    setQuestionsModalData([]);
    setQuestionsModalError(false);
    setQuestionsModalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/survey/${survey.pin}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status} al obtener las preguntas`);
      }
      const data = await response.json();
      setQuestionsModalData(Array.isArray(data.questions) ? data.questions : []);
    } catch (error) {
      console.error('Error al cargar las preguntas de la encuesta:', error);
      setQuestionsModalError(true);
    } finally {
      setQuestionsModalLoading(false);
    }
  };

  const formatLocalDate = (dateStr) => {
    const date = new Date(dateStr);
    const offset = date.getTimezoneOffset(); // minutos
    date.setMinutes(date.getMinutes() - offset);
    return date.toISOString().slice(0, 10);
  };

  const handleCreateSurveySubmit = async (surveyData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(surveyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido del servidor' }));
        throw new Error(errorData.error || response.statusText);
      }

      const data = await response.json();
      // Aviso por email a los participantes del Excel, igual que al crear desde plantilla o desde cero
      sendPinEmails({ emails: surveyData.participantes && surveyData.participantes.emails, pin: data.pin, language });
      setNotice({
        variant: 'confirm',
        title: t('dash_alert_diagnostic_created'),
        text: `${t('dash_th_pin')}: ${data.pin}`
      });
      
      // Limpiar localStorage
      localStorage.removeItem('selectedQuestions');
      localStorage.removeItem('surveyTopic');
      
      // Cerrar modal y actualizar encuestas
      setShowCreateSurveyModal(false);
      setSavedQuestions(null);
      setSavedTopic('');
      fetchSurveys();
    } catch (err) {
      console.error('Error al crear el diagnóstico:', err);
      setNotice({ variant: 'warning', title: t('dash_alert_create_error'), text: err.message });
    }
  };

  const getSurveyStatus = (survey) => {
    const now = new Date();
    const endDate = new Date(survey.fecha_fin);
    
    // Extraer el número de participantes del formato "13/0"
    let participantes = 0;
    if (typeof survey.participantes === 'string' && survey.participantes.includes('/')) {
      participantes = parseInt(survey.participantes.split('/')[0]) || 0;
    } else {
      participantes = parseInt(survey.participantes) || 0;
    }
    
    const hasParticipants = participantes > 0;
    const isFinished = endDate < now;
    
    if (survey.download || (isFinished && hasParticipants)) {
      return 'results_available';
    } else if (isFinished && !hasParticipants) {
      return 'no_data';
    } else if (!hasParticipants) {
      return 'no_data';
    } else {
      return 'open';
    }
  };

  const openEditModal = (survey) => {
    setEditSurvey({
      id: survey.id_encuesta,
      titulo: survey.titulo,
      fecha_inicio: formatLocalDate(survey.fecha_inicio),
      fecha_fin: formatLocalDate(survey.fecha_fin)
    });
    setIsEditModalOpen(true);
  };

  // Función para abrir el modal de compartir
  const handleShareSurvey = (survey) => {
    setShareSurvey(survey);
    setShowShareModal(true);
  };

  // Función para cerrar el modal de compartir
  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareSurvey(null);
  };

  const locale = localeOf(language);
  const formatDateRange = (start, end) => {
    const year = new Date().getFullYear();
    const fmt = (value) => {
      const date = new Date(value);
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(date.getFullYear() !== year ? { year: 'numeric' } : {})
      });
    };
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleCopyPin = async (survey) => {
    try {
      await navigator.clipboard.writeText(String(survey.pin));
      setCopiedPinId(survey.id_encuesta);
      setTimeout(() => setCopiedPinId((current) => (current === survey.id_encuesta ? null : current)), 1600);
    } catch (error) {
      console.error('No se pudo copiar el PIN:', error);
    }
  };

  const statusCounts = STATUS_FILTERS.reduce((acc, filter) => {
    acc[filter] = filter === 'all'
      ? filteredSurveys.length
      : filteredSurveys.filter((survey) => getSurveyStatus(survey) === filter).length;
    return acc;
  }, {});

  const visibleSurveys = statusFilter === 'all'
    ? filteredSurveys
    : filteredSurveys.filter((survey) => getSurveyStatus(survey) === statusFilter);

  const totalPages = Math.max(1, Math.ceil(visibleSurveys.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pagedSurveys = visibleSurveys.slice(pageStart, pageStart + pageSize);

  const changePageSize = (size) => {
    setPageSize(size);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch (error) {
      console.error('No se pudo guardar el tamaño de página:', error);
    }
  };

  const goToPage = (nextPage) => {
    setPage(nextPage);
    const toolbar = toolbarRef.current;
    if (toolbar && toolbar.getBoundingClientRect().top < 0) {
      toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="home-container tp-dash">
      <div className="dashboard-container">
        <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('adminheader_dashboard')}</SectionEyebrow>
            <p className="tp-dash__lead">{t('dash_lead')}</p>
          </div>
          <button
            type="button"
            className="btn btn--primary tp-dash__new"
            onClick={() => { trackEvent('button_click', 'create_survey'); navigate('/choose'); }}
          >
            <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
            {t('dash_new')}
          </button>
        </header>

        <div className="tp-dash__toolbar" ref={toolbarRef}>
          <div className="tp-dash__tabs" role="group" aria-label={t('dash_filter_label')}>
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                className={`tp-dash__tab ${statusFilter === filter ? 'is-active' : ''}`.trim()}
                aria-pressed={statusFilter === filter}
                onClick={() => setStatusFilter(filter)}
              >
                {t(`dash_filter_${filter}`)}
                <span className="tp-dash__tab-count">{statusCounts[filter]}</span>
              </button>
            ))}
          </div>

          <div className="tp-dash__controls">
            <label className="tp-dash__search">
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              <span className="tp-sr-only">{t('dash_search_placeholder')}</span>
              <input
                type="search"
                placeholder={t('dash_search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
            <SelectMenu
              className="tp-dash__sort"
              value={sortOption}
              onChange={setSortOption}
              label={t('dash_sort_label')}
              placeholder={t('dash_sort_by')}
              icon={<ArrowUpDown size={15} strokeWidth={2} />}
              options={[
                { value: '', label: t('dash_sort_default') },
                { value: 'titulo', label: t('dash_sort_title') },
                { value: 'fecha_inicio', label: t('dash_sort_start_date') },
                { value: 'fecha_fin', label: t('dash_sort_end_date') }
              ]}
            />
          </div>
        </div>

        {!surveysLoaded && <Loader />}

        {surveysLoaded && loadError && surveys.length === 0 && (
          <div className="tp-dash__empty" role="alert">
            <span className="tp-dash__empty-icon" aria-hidden="true">
              <TriangleAlert size={26} strokeWidth={1.75} />
            </span>
            <p className="tp-dash__empty-title">{t('dash_load_error_title')}</p>
            <p className="tp-dash__empty-text">{t('dash_load_error_text')}</p>
            <button type="button" className="btn btn--outline tp-dash__new" onClick={fetchSurveys}>
              <RotateCw size={16} strokeWidth={2.25} aria-hidden="true" />
              {t('dash_retry')}
            </button>
          </div>
        )}

        {surveysLoaded && !loadError && surveys.length === 0 && (
          <div className="tp-dash__empty">
            <span className="tp-dash__empty-icon" aria-hidden="true">
              <ClipboardList size={26} strokeWidth={1.75} />
            </span>
            <p className="tp-dash__empty-title">{t('dash_empty_title')}</p>
            <p className="tp-dash__empty-text">{t('dash_empty_text')}</p>
            <button
              type="button"
              className="btn btn--primary tp-dash__new"
              onClick={() => { trackEvent('button_click', 'create_survey'); navigate('/choose'); }}
            >
              <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              {t('dash_new')}
            </button>
          </div>
        )}

        {surveys.length > 0 && visibleSurveys.length === 0 && (
          <p className="tp-dash__no-match">{t('dash_empty_filter')}</p>
        )}

        {visibleSurveys.length > 0 && (
          <ul className="tp-dash__list">
            {pagedSurveys.map((survey) => {
              const status = getSurveyStatus(survey);
              const hasResults = status === 'results_available' || status === 'open';
              const { done, invited } = parseParticipants(survey.participantes);
              const progress = invited > 0 ? Math.min(100, Math.round((done / invited) * 100)) : 0;
              const canPresent = Boolean(survey.pin) && !(new Date(survey.fecha_fin) < new Date() && !(new Date(survey.fecha_inicio).toDateString() === new Date().toDateString() && new Date(survey.fecha_fin).toDateString() === new Date().toDateString()));
              const share = () => { trackEvent('button_click', 'share_survey', { survey_id: survey.id_encuesta }); handleShareSurvey(survey); };
              const viewResults = () => { trackEvent('button_click', 'view_results', { survey_id: survey.id_encuesta }); navigate(`/dashboard/${survey.id_encuesta}`); };

              return (
                <li
                  key={survey.id_encuesta}
                  id={`encuesta-row-${survey.id_encuesta}`}
                  className={`tp-dash__item ${highlightedSurveyId === survey.id_encuesta ? 'is-highlighted' : ''}`.trim()}
                >
                  <div className="tp-dash__main">
                    <div className="tp-dash__title-row">
                      <h3 className="tp-dash__title">{survey.titulo}</h3>
                      <span className={`tp-dash__status is-${status}`}>{t(`dash_status_${status}`)}</span>
                    </div>
                    <div className="tp-dash__meta">
                      <span className="tp-dash__pin">
                        {t('dash_th_pin')} <strong>{survey.pin}</strong>
                        <button
                          type="button"
                          className="tp-dash__copy"
                          onClick={() => handleCopyPin(survey)}
                          aria-label={t('dash_copy_pin')}
                          title={t('dash_copy_pin')}
                        >
                          {copiedPinId === survey.id_encuesta
                            ? <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                            : <Copy size={14} strokeWidth={2} aria-hidden="true" />}
                        </button>
                      </span>
                      <span className="tp-dash__dates">
                        <CalendarDays size={14} strokeWidth={2} aria-hidden="true" />
                        {formatDateRange(survey.fecha_inicio, survey.fecha_fin)}
                      </span>
                    </div>
                  </div>

                  <div className="tp-dash__progress">
                    {invited > 0 && (
                      <span className="tp-dash__bar" aria-hidden="true">
                        <span className="tp-dash__bar-fill" style={{ width: `${progress}%` }} />
                      </span>
                    )}
                    <span className="tp-dash__responses">
                      <Users size={14} strokeWidth={2} aria-hidden="true" />
                      {responsesText(t, done, invited)}
                    </span>
                  </div>

                  <div className="tp-dash__actions">
                    {status === 'results_available' ? (
                      <button type="button" className="btn btn--primary btn--sm tp-dash__primary" onClick={viewResults}>
                        <BarChart3 size={15} strokeWidth={2} aria-hidden="true" />
                        {t('dash_view_results')}
                      </button>
                    ) : (
                      <button type="button" className="btn btn--outline btn--sm tp-dash__primary" onClick={share}>
                        <Share2 size={15} strokeWidth={2} aria-hidden="true" />
                        {t('dash_share')}
                      </button>
                    )}

                    <ActionMenu
                      label={t('dash_more_actions')}
                      groups={[
                        {
                          key: 'results',
                          title: t('dash_menu_results'),
                          items: hasResults
                            ? [
                                { key: 'results', label: t('dash_view_results'), icon: <BarChart3 size={16} />, onClick: viewResults },
                                { key: 'analysis', label: t('dash_view_analysis'), icon: <Sparkles size={16} />, onClick: () => { trackEvent('button_click', 'view_hume_analysis', { survey_id: survey.id_encuesta }); navigate(`/dashboard/${survey.id_encuesta}?tab=preguntas`); } },
                                { key: 'responses', label: t('dash_view_responses'), icon: <MessageSquareText size={16} />, onClick: () => { trackEvent('button_click', 'view_responses', { survey_id: survey.id_encuesta }); navigate(`/dashboard/${survey.id_encuesta}?tab=respuestas`); } },
                                { key: 'pdf', label: t('dash_download_pdf'), icon: <FileDown size={16} />, onClick: () => { trackEvent('button_click', 'download_pdf', { survey_id: survey.id_encuesta }); downloadPdf(survey); } }
                              ]
                            : [{ key: 'no-data', label: t('dash_insufficient_info'), icon: <Info size={16} />, disabled: true, onClick: () => {} }]
                        },
                        {
                          key: 'survey',
                          title: t('dash_menu_diagnostic'),
                          items: [
                            ...(status === 'results_available' ? [{ key: 'share', label: t('dash_share'), icon: <Share2 size={16} />, onClick: share }] : []),
                            { key: 'questions', label: t('dash_view_questions'), icon: <ListChecks size={16} />, disabled: !survey.pin, onClick: () => { trackEvent('button_click', 'view_questions', { survey_id: survey.id_encuesta }); handleViewQuestions(survey); } },
                            { key: 'present', label: t('dash_present'), icon: <Presentation size={16} />, disabled: !canPresent, onClick: () => handlePresentation(survey) },
                            { key: 'edit', label: t('dash_modify'), icon: <Pencil size={16} />, onClick: () => { trackEvent('button_click', 'edit_survey', { survey_id: survey.id_encuesta }); openEditModal(survey); } }
                          ]
                        },
                        {
                          key: 'danger',
                          items: [
                            { key: 'delete', label: t('dash_delete'), icon: <Trash2 size={16} />, danger: true, onClick: () => { trackEvent('button_click', 'delete_survey', { survey_id: survey.id_encuesta }); handleDelete(survey); } }
                          ]
                        }
                      ]}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={goToPage}
          total={visibleSurveys.length}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={changePageSize}
          summary={t('dash_page_summary')
            .replace('{from}', pageStart + 1)
            .replace('{to}', pageStart + pagedSurveys.length)
            .replace('{total}', visibleSurveys.length)}
          labels={{
            nav: t('dash_pagination_label'),
            prev: t('dash_page_prev'),
            next: t('dash_page_next'),
            page: (n) => t('dash_page_n').replace('{n}', n),
            show: t('dash_page_show'),
            perPage: t('dash_page_per_page'),
            perPageLabel: t('dash_page_size_label')
          }}
        />

        <EditSurveyModal
          survey={isEditModalOpen ? editSurvey : null}
          onClose={() => setIsEditModalOpen(false)}
          onSaved={fetchSurveys}
        />

        <PresentationModal
          survey={isPresentationModalOpen && presentationSurvey
            ? (surveys.find((s) => s.id_encuesta === presentationSurvey.id_encuesta) || presentationSurvey)
            : null}
          dateRange={presentationSurvey ? formatDateRange(presentationSurvey.fecha_inicio, presentationSurvey.fecha_fin) : ''}
          onClose={() => setIsPresentationModalOpen(false)}
        />

        <QuestionsModal
          survey={isQuestionsModalOpen ? questionsModalSurvey : null}
          loading={questionsModalLoading}
          error={questionsModalError}
          questions={questionsModalData}
          onClose={() => setIsQuestionsModalOpen(false)}
        />

        <DeleteSurveyModal
          open={isShareModalOpen2}
          pin={currentPin}
          titulo={currentTitulo}
          onClose={() => setIsShareModalOpen2(false)}
          onDeleted={fetchSurveys}
        />

        <AppModal
          open={Boolean(notice)}
          onOpenChange={(open) => { if (!open) setNotice(null); }}
          variant={notice ? notice.variant : 'info'}
          title={notice ? notice.title : ''}
          description={notice ? notice.text : ''}
          confirmText={t('common_understood')}
          onConfirm={() => setNotice(null)}
        />

        {/* Create Survey Modal for saved questions */}
        {showCreateSurveyModal && savedQuestions && (
          <CreateSurveyModal
            onClose={() => {
              setShowCreateSurveyModal(false);
              setSavedQuestions(null);
              setSavedTopic('');
              localStorage.removeItem('selectedQuestions');
              localStorage.removeItem('surveyTopic');
            }}
            onSubmit={handleCreateSurveySubmit}
            selectedQuestions={savedQuestions}
            initialTopic={savedTopic}
          />
        )}

        {/* Notificación de estado de token */}
        <TokenStatusNotification />

        {/* Modal de compartir */}
        <ShareSurveyModal
          survey={showShareModal ? shareSurvey : null}
          onClose={handleCloseShareModal}
        />

      </div>

      {pdfUi}

    </div>
  );
};

export default Dashboard;
